import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// molecule — DWR form field: label uppercase Cinzel + control border-bottom only.
// Pattern .field mockup (styles.css 823-842).
//
// Lo skin del control nativo (<input> / <textarea>) è applicato via Tailwind
// arbitrary variants ([&_input]:..., [&_textarea]:...): bg trasparente + border
// solo bottom 1.5px ink + focus border accent-dwr.
//
// Il `<select>` shadcn (Radix Select) NON eredita questo skin — i consumer che
// vogliono uno select stilizzato DWR devono customizzarlo manualmente o usare
// altri pattern (es. ChoiceRow). `<Field>` qui copre il 90% dei casi (text inputs
// e textarea liberi).
//
// Rule of three soddisfatta in PR 6: name + title + bond text + bond target
// (4 istanze immediate) + refactor opportunistico di race custom (PR 4), precept
// textarea (PR 5), custom aspetto (PR 5).

interface FieldProps {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, hint, children, className }: FieldProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-[0.35rem]',
        // Skin descendant per input nativo:
        '[&_input]:font-serif [&_input]:text-ink [&_input]:placeholder:text-ink-faint',
        '[&_input]:border-ink [&_input]:focus:border-accent-dwr',
        '[&_input]:border-0 [&_input]:border-b-[1.5px] [&_input]:bg-transparent',
        '[&_input]:px-[0.1em] [&_input]:pt-[0.4em] [&_input]:pb-[0.3em]',
        '[&_input]:text-base [&_input]:outline-none',
        // Skin descendant per textarea:
        '[&_textarea]:font-serif [&_textarea]:text-ink [&_textarea]:placeholder:text-ink-faint',
        '[&_textarea]:border-ink [&_textarea]:focus:border-accent-dwr',
        '[&_textarea]:border-0 [&_textarea]:border-b-[1.5px] [&_textarea]:bg-transparent',
        '[&_textarea]:px-[0.1em] [&_textarea]:pt-[0.4em] [&_textarea]:pb-[0.3em]',
        '[&_textarea]:text-base [&_textarea]:outline-none',
        '[&_textarea]:min-h-[60px] [&_textarea]:resize-y',
        // **FI-50 (2026-05-22)** — required+empty: bordo accent-dwr per
        // segnalare campo da compilare. Usato da `NarrativeTextField`
        // attivando `aria-invalid` sull'input/textarea.
        '[&_input[aria-invalid=true]]:border-accent-dwr',
        '[&_textarea[aria-invalid=true]]:border-accent-dwr',
        className,
      )}
    >
      <label
        htmlFor={htmlFor}
        className="font-display text-ink-soft text-[0.75rem] tracking-[0.1em] uppercase"
      >
        {label}
      </label>
      {children}
      {hint ? <p className="text-ink-faint text-xs italic">{hint}</p> : null}
    </div>
  );
}
